import React, { useState, useEffect, useRef, useMemo, useCallback, lazy, Suspense } from 'react';
import kioLogo from './assets/kio-logo.svg';
import { AppProvider, Frame, Navigation, TopBar, Text, InlineStack, Icon } from '@shopify/polaris';
import {
  HomeIcon,
  ProductIcon,
  OrderIcon,
  DiscountIcon,
  PersonIcon,
  EmailNewsletterIcon,
  ChartVerticalIcon,
  DeliveryIcon,
  LocationIcon,
  ReceiptIcon,
  StoreIcon,
  PointOfSaleIcon,
  ChatIcon,
  SearchIcon,
  ExternalIcon,
  ViewIcon,
  TeamIcon,
  AffiliateIcon,
  FolderIcon,
  WalletIcon,
  TargetIcon,
} from '@shopify/polaris-icons';

import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth, getPendingContactVerification } from './context/AuthContext';
import { buildStorefrontUrl, resolveStorefrontHost } from './utils/storeDomain';
import {
  formatPackDisplayName,
  getUserPackName,
  hasActivePosPack,
} from './utils/subscriptionPlan';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import { useAppT, buildSearchIndex } from './i18n/appT';
import { apiFetch, apiAcceptStaffInvitation } from './services/api';
import {
  getPendingStaffToken,
  clearPendingStaffToken,
  setStaffInvitationError,
} from './utils/staffInvitation';
import { isPathAllowed, firstAllowedPath, canAccessPath } from './utils/staffPermissions';
import { isPackPathAllowed } from './utils/subscriptionPlan';
import { useStorefrontAccess } from './hooks/useStorefrontAccess';
import { useKioAgentAccess } from './hooks/useKioAgentAccess';
import { ANALYTICS_SECTIONS } from './constants/analytics';
import ContactVerificationBanner from './components/ContactVerificationBanner';
import PhoneRequiredGate from './components/PhoneRequiredGate';
import TrialBanner from './components/TrialBanner';
import ThemeUpdateNotificationBanner from './components/ThemeUpdateNotificationBanner';
import PwaInstallBanner from './components/PwaInstallBanner';
import GoogleOAuthShell from './components/GoogleOAuthShell';
import RouteTransitionShell from './components/RouteTransitionShell';
import PageRouteFallback from './components/PageRouteFallback';
import PaymentReturnHandler from './components/PaymentReturnHandler';

// Public/auth pages are lazy too: logged-in users (the common case) never pay
// for Login/Signup/Onboarding code — ~100 KB incl. the algeriaGeo dataset.
const Login = lazy(() => import('./pages/Login'));
const Signup = lazy(() => import('./pages/Signup'));
const VerifyAccount = lazy(() => import('./pages/VerifyAccount'));
const StoreSelect = lazy(() => import('./pages/StoreSelect'));
const StoreOnboarding = lazy(() => import('./pages/StoreOnboarding'));

const Home = lazy(() => import('./pages/Home'));
const Products = lazy(() => import('./pages/Products'));
const Inventory = lazy(() => import('./pages/Inventory'));
const Categories = lazy(() => import('./pages/Categories'));
const Collections = lazy(() => import('./pages/Collections'));
const AddProduct = lazy(() => import('./pages/AddProduct'));
const Orders = lazy(() => import('./pages/Orders'));
const AbandonedCarts = lazy(() => import('./pages/AbandonedCarts'));
const RefundedOrders = lazy(() => import('./pages/RefundedOrders'));
const KioStore = lazy(() => import('./pages/KioStore'));
const KioWallet = lazy(() => import('./pages/KioWallet'));
const KioPos = lazy(() => import('./pages/KioPos'));
// Canal visible selon l'accès KIOAGENT du compte (`kioagent` de GET /connect/user/)
const KioAgent = lazy(() => import('./pages/KioAgent'));
const AiCredits = lazy(() => import('./pages/AiCredits'));
const ThemeCustomizer = lazy(() => import('./pages/ThemeCustomizer'));
const CodeEditor = lazy(() => import('./pages/CodeEditor'));
const ThemesManager = lazy(() => import('./pages/ThemesManager'));
const AddTheme = lazy(() => import('./pages/AddTheme'));
const LandingPageEditor = lazy(() => import('./pages/LandingPageEditor'));
const NavigationEditor = lazy(() => import('./pages/NavigationEditor'));
const StorePages = lazy(() => import('./pages/StorePages'));
const FunnelsManager = lazy(() => import('./pages/FunnelsManager'));
const AnalyticsLayout = lazy(() => import('./components/analytics/AnalyticsLayout'));
const Analytics = lazy(() => import('./pages/Analytics'));
const AnalyticsFinance = lazy(() => import('./pages/AnalyticsFinance'));
const AnalyticsOrders = lazy(() => import('./pages/AnalyticsOrders'));
const AnalyticsClients = lazy(() => import('./pages/AnalyticsClients'));
const AnalyticsProducts = lazy(() => import('./pages/AnalyticsProducts'));
const AnalyticsChannels = lazy(() => import('./pages/AnalyticsChannels'));
const Staff = lazy(() => import('./pages/Staff'));
const MyEarnings = lazy(() => import('./pages/MyEarnings'));
const Shipping = lazy(() => import('./pages/Shipping'));
const Tracking = lazy(() => import('./pages/Tracking'));
const NoestDashboard = lazy(() => import('./pages/NoestDashboard'));
const EcotrackDashboard = lazy(() => import('./pages/EcotrackDashboard'));
const CarrierZonesPage = lazy(() => import('./pages/CarrierZonesPage'));
const Suppliers = lazy(() => import('./pages/Suppliers'));
const PurchaseOrders = lazy(() => import('./pages/PurchaseOrders'));
const Invoices = lazy(() => import('./pages/Invoices'));
const Affiliate = lazy(() => import('./pages/Affiliate'));
const PersonalSpace = lazy(() => import('./pages/PersonalSpace'));
const Profile = lazy(() => import('./pages/Profile'));
const Support = lazy(() => import('./pages/Support'));
const Discounts = lazy(() => import('./pages/Discounts'));
const Customers = lazy(() => import('./pages/Customers'));
const AllNotifications = lazy(() => import('./pages/AllNotifications'));
const AllActivity = lazy(() => import('./pages/AllActivity'));
const Newsletter = lazy(() => import('./pages/Newsletter'));
const MarketingCampaigns = lazy(() => import('./pages/MarketingCampaigns'));
const SmsCampaigns       = lazy(() => import('./pages/SmsCampaigns'));
const Checkout           = lazy(() => import('./pages/Checkout'));

// ── Route prefetching ─────────────────────────────────────────────────────────
// Same import() calls as the lazy() declarations above: webpack resolves them
// to the same chunks, so warming one here makes the later lazy() mount instant.
const ROUTE_PRELOADERS = {
  '/': () => import('./pages/Home'),
  '/orders': () => import('./pages/Orders'),
  '/orders/abandoned': () => import('./pages/AbandonedCarts'),
  '/orders/refunded': () => import('./pages/RefundedOrders'),
  '/products': () => import('./pages/Products'),
  '/products/inventory': () => import('./pages/Inventory'),
  '/products/categories': () => import('./pages/Categories'),
  '/products/collections': () => import('./pages/Collections'),
  '/products/add': () => import('./pages/AddProduct'),
  '/discounts': () => import('./pages/Discounts'),
  '/customers': () => import('./pages/Customers'),
  '/staff': () => import('./pages/Staff'),
  '/my-earnings': () => import('./pages/MyEarnings'),
  '/analytics/vue-ensemble': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/Analytics')]),
  '/analytics/finance': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/AnalyticsFinance')]),
  '/analytics/commandes': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/AnalyticsOrders')]),
  '/analytics/clients': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/AnalyticsClients')]),
  '/analytics/produits': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/AnalyticsProducts')]),
  '/analytics/canaux': () => Promise.all([import('./components/analytics/AnalyticsLayout'), import('./pages/AnalyticsChannels')]),
  '/channels/kiostore': () => import('./pages/KioStore'),
  '/wallet': () => import('./pages/KioWallet'),
  '/channels/kiopos': () => import('./pages/KioPos'),
  '/channels/kioagent': () => import('./pages/KioAgent'),
  '/ai-credits': () => import('./pages/AiCredits'),
  '/shipping': () => import('./pages/Shipping'),
  '/tracking': () => import('./pages/Tracking'),
  '/purchases/purchase-orders': () => import('./pages/PurchaseOrders'),
  '/purchases/invoices': () => import('./pages/Invoices'),
  '/purchases/suppliers': () => import('./pages/Suppliers'),
  '/marketing/bulletins': () => import('./pages/Newsletter'),
  '/marketing/campagnes': () => import('./pages/MarketingCampaigns'),
  '/marketing/sms':       () => import('./pages/SmsCampaigns'),
  '/affiliate': () => import('./pages/Affiliate'),
  '/personal-space': () => import('./pages/PersonalSpace'),
  '/notifications': () => import('./pages/AllNotifications'),
  '/profile': () => import('./pages/Profile'),
  '/support': () => import('./pages/Support'),
};

const preloadedRoutes = new Set();

function preloadRoute(pathname) {
  if (!pathname) return;
  const key = ROUTE_PRELOADERS[pathname]
    ? pathname
    : Object.keys(ROUTE_PRELOADERS).find((p) => p !== '/' && pathname.startsWith(p));
  if (!key || preloadedRoutes.has(key)) return;
  preloadedRoutes.add(key);
  ROUTE_PRELOADERS[key]();
}

function handleNavPrefetch(e) {
  const link = e.target.closest?.('a[href^="/"]');
  if (link) preloadRoute(link.getAttribute('href'));
}
 const YoutubeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M23.5 6.2C23.2 4.9 22.2 3.9 20.9 3.6C18.6 3 12 3 12 3C12 3 5.4 3 3.1 3.6C1.8 3.9 0.8 4.9 0.5 6.2C0 8.5 0 12 0 12C0 12 0 15.5 0.5 17.8C0.8 19.1 1.8 20.1 3.1 20.4C5.4 21 12 21 12 21C12 21 18.6 21 20.9 20.4C22.2 20.1 23.2 19.1 23.5 17.8C24 15.5 24 12 24 12C24 12 24 8.5 23.5 6.2Z"
      fill="#FF0000"
    />
    <path
      d="M9.6 15.9L15.8 12L9.6 8.1V15.9Z"
      fill="#FFFFFF"
    />
  </svg>
);
const WhatsAppIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="#25D366"
      d="M20.52 3.449A11.818 11.818 0 0 0 12.04 0C5.495 0 .16 5.335.16 11.88c0 2.093.547 4.137 1.586 5.94L.05 24l6.31-1.655a11.89 11.89 0 0 0 5.68 1.445h.005c6.545 0 11.88-5.335 11.88-11.88a11.82 11.82 0 0 0-3.405-8.461Z"
    />

    <path
      fill="#FFFFFF"
      d="M12.045 21.785h-.004a9.88 9.88 0 0 1-5.034-1.38l-.361-.214-3.744.982 1-3.652-.235-.374a9.87 9.87 0 0 1-1.515-5.267c0-5.45 4.435-9.885 9.89-9.885a9.83 9.83 0 0 1 7.006 2.905 9.83 9.83 0 0 1 2.899 7.005c-.004 5.45-4.439 9.88-9.902 9.88Z"
    />

    <path
      fill="#25D366"
      d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.075-.792.372-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Z"
    />
  </svg>
);
function UserAvatar({ user, size = 30, fontSize = 12 }) {
  const initials = [user.firstName, user.lastName]
    .filter(Boolean).map(s => s[0].toUpperCase()).join('') || (user.username?.[0] ?? 'K').toUpperCase();
  const bg = user.color || '#111';

  if (user.avatar) {
    return (
      <img
        src={user.avatar}
        alt=""
        style={{
          width: size, height: size, borderRadius: '50%',
          objectFit: 'cover', flexShrink: 0,
        }}
      />
    );
  }

  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: bg, color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize, fontWeight: 700, flexShrink: 0, letterSpacing: 0.5,
    }}>
      {initials}
    </div>
  );
}

function UserDropdown({ user, currentStore, logout, clearStore }) {
  const { lang } = useLanguage();
  const t = useAppT(lang);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const { openStorefront, upgradeModal } = useStorefrontAccess();

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fullName = [user.firstName, user.lastName].filter(Boolean).join(' ') || user.username || '';
  const storefrontDomain = useMemo(() => resolveStorefrontHost(currentStore), [currentStore]);
  const activePackLabel = useMemo(
    () => formatPackDisplayName(getUserPackName(user), lang),
    [user, lang],
  );
  const showPosPack = useMemo(() => hasActivePosPack(user), [user]);

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center', alignSelf: 'center' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '4px 6px', borderRadius: 8,
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(0,0,0,0.06)'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; }}
      >
        <UserAvatar user={user} size={30} fontSize={12} />
        {/* Name + store — hidden on mobile */}
        <div className="kio-user-labels" style={{ textAlign: 'left', lineHeight: 1.2 }}>
          <div style={{ fontSize: 12, fontWeight: 650, color: '#111', whiteSpace: 'nowrap' }}>{fullName}</div>
          <div style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>{currentStore?.name}</div>
        </div>
        {/* Chevron */}
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', flexShrink: 0 }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="kio-user-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          minWidth: 220, zIndex: 999, overflow: 'hidden',
          animation: 'kio-dd-in 0.15s ease both',
        }}>
          <style>{`@keyframes kio-dd-in { from { opacity:0; transform:translateY(-6px) } to { opacity:1; transform:translateY(0) } }`}</style>

          {/* User info header */}
          <div style={{ padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UserAvatar user={user} size={36} fontSize={13} />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 650, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{fullName}</div>
                <div style={{ fontSize: 11, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.email}</div>
                {(activePackLabel || showPosPack) && (
                  <div style={{ fontSize: 10, fontWeight: 600, color: '#7c3aed', marginTop: 2 }}>
                    {activePackLabel}
                    {showPosPack ? `${activePackLabel ? ' · ' : ''}POS` : ''}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Store badge */}
          <div style={{ padding: '10px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#303030" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l1-6h16l1 6" /><path d="M3 9a2 2 0 0 0 2 2 2 2 0 0 0 2-2 2 2 0 0 0 2 2 2 2 0 0 0 2-2 2 2 0 0 0 2 2 2 2 0 0 0 2-2" />
                  <path d="M5 21V11m14 10V11" /><rect x="9" y="14" width="6" height="7" rx="1" />
                </svg>
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#303030', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{currentStore?.name}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{storefrontDomain}</div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ padding: '6px 8px' }}>
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>}
              label={t('view_store')}
              onClick={() => { setOpen(false); openStorefront(); }}
            />
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>}
              label={t('change_store')}
              onClick={() => { setOpen(false); clearStore(); navigate('/store-select'); }}
            />
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>}
              label={t('account_settings')}
              onClick={() => { setOpen(false); navigate('/profile'); }}
            />
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z"/><path d="M19 17l.8 2.2L22 20l-2.2.8L19 23l-.8-2.2L16 20l2.2-.8z"/></svg>}
              label={t('nav_ai_credits')}
              onClick={() => { setOpen(false); navigate('/ai-credits'); }}
            />
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>}
              label={t('support')}
              onClick={() => { setOpen(false); navigate('/support'); }}
            />
          </div>

          {/* Logout */}
          <div style={{ padding: '6px 8px', borderTop: '1px solid #f3f4f6' }}>
            <DropdownItem
              icon={<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>}
              label={t('logout')}
              onClick={() => { setOpen(false); logout(); }}
              danger
            />
          </div>
        </div>
      )}
      {upgradeModal}
    </div>
  );
}

function DropdownItem({ icon, label, onClick, danger }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        width: '100%', padding: '8px 10px', borderRadius: 7,
        background: hovered ? (danger ? '#fff1f0' : '#f3f4f6') : 'none',
        border: 'none', cursor: 'pointer', textAlign: 'left',
        color: danger ? '#d72c0d' : '#303030',
        fontSize: 13, fontWeight: 500, transition: 'background 0.12s',
      }}
    >
      <span style={{ flexShrink: 0, opacity: 0.75 }}>{icon}</span>
      {label}
    </button>
  );
}

const FlagFR = () => (
  <svg width="20" height="14" viewBox="0 0 30 20" style={{ borderRadius: 2, flexShrink: 0 }}>
    <rect width="10" height="20" fill="#002395"/>
    <rect x="10" width="10" height="20" fill="#fff"/>
    <rect x="20" width="10" height="20" fill="#ED2939"/>
  </svg>
);
const FlagEN = () => (
  <svg width="20" height="14" viewBox="0 0 60 40" style={{ borderRadius: 2, flexShrink: 0 }}>
    <rect width="60" height="40" fill="#012169"/>
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#fff" strokeWidth="9"/>
    <path d="M0,0 L60,40 M60,0 L0,40" stroke="#C8102E" strokeWidth="6"/>
    <path d="M30,0 V40 M0,20 H60" stroke="#fff" strokeWidth="14"/>
    <path d="M30,0 V40 M0,20 H60" stroke="#C8102E" strokeWidth="9"/>
  </svg>
);
const FlagDZ = () => (
  <svg width="20" height="14" viewBox="0 0 30 20" style={{ borderRadius: 2, flexShrink: 0 }}>
    <rect width="15" height="20" fill="#006233"/>
    <rect x="15" width="15" height="20" fill="#fff"/>
    {/* Croissant : grand arc extérieur − arc intérieur */}
    <path d="M17.25,6.69 A5,5 0 1,0 17.25,13.31 A4,4 0 0,1 17.25,6.69 Z" fill="#D21034"/>
    {/* Étoile à 5 branches */}
    <polygon
      transform="translate(21,10) scale(0.9)"
      points="0,-2 0.45,-0.62 1.90,-0.62 0.72,0.24 1.18,1.62 0,0.76 -1.18,1.62 -0.72,0.24 -1.90,-0.62 -0.45,-0.62"
      fill="#D21034"
    />
  </svg>
);

const LANGS = [
  { code: 'fr', label: 'Français', Flag: FlagFR },
  { code: 'en', label: 'English',  Flag: FlagEN },
  { code: 'ar', label: 'العربية',  Flag: FlagDZ },
];

function LangDropdown({ lang, setLang }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const current = LANGS.find(l => l.code === lang) || LANGS[0];

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6,
          background: 'none', border: '1px solid #e5e7eb',
          borderRadius: 8, cursor: 'pointer',
          padding: '5px 10px', transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#d1d5db'; }}
        onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
      >
        <current.Flag />
        <span style={{ fontSize: 12, fontWeight: 650, color: '#303030', letterSpacing: 0.3 }}>
          {current.code.toUpperCase()}
        </span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="kio-lang-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
          minWidth: 150, zIndex: 999, overflow: 'hidden',
          animation: 'kio-dd-in 0.15s ease both',
          padding: '4px',
        }}>
          {LANGS.map(l => (
            <button
              key={l.code}
              onClick={() => { setLang(l.code); setOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                width: '100%', padding: '8px 10px', borderRadius: 7,
                background: l.code === lang ? '#f3f4f6' : 'none',
                border: 'none', cursor: 'pointer', textAlign: 'left',
                transition: 'background 0.1s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
              onMouseLeave={e => { e.currentTarget.style.background = l.code === lang ? '#f3f4f6' : 'none'; }}
            >
              <l.Flag />
              <span style={{ fontSize: 13, fontWeight: 500, color: '#303030' }}>{l.label}</span>
              {l.code === lang && (
                <svg style={{ marginLeft: 'auto' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const NOTIF_ICONS = {
  order:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 2h4v2H4V5h4l1-2z"/><path d="M5 7l1 13h12l1-13"/><path d="M10 11v6M14 11v6"/></svg>,
  payment: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><path d="M7 15h4"/></svg>,
  alert:   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><triangle/><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  stock: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  purchase: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6l1 2h4v2H4V5h4l1-2z"/><path d="M5 7l1 13h12l1-13"/><path d="M10 11h4M10 15h4"/></svg>,
  customer: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  theme: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18.37 2.63a2.1 2.1 0 0 1 3 3L9.65 17.35a4 4 0 0 1-1.68 1L4 19.5l1.15-3.97a4 4 0 0 1 1-1.68z"/><path d="M14.5 6.5l3 3"/></svg>,
  info:    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  subscription: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><line x1="2" y1="10" x2="22" y2="10"/><path d="M15 15l1 2 2-1"/></svg>,
};

const NOTIF_TONES = {
  order: { bg: '#eaf4ff', color: '#1d4ed8' },
  payment: { bg: '#ecfeff', color: '#0f766e' },
  alert: { bg: '#fff1f2', color: '#be123c' },
  stock: { bg: '#fff7ed', color: '#c2410c' },
  purchase: { bg: '#f5f3ff', color: '#6d28d9' },
  customer: { bg: '#eef2ff', color: '#4338ca' },
  theme: { bg: '#ecfdf5', color: '#047857' },
  subscription: { bg: '#faf5ff', color: '#7e22ce' },
  info: { bg: '#f3f4f6', color: '#4b5563' },
};

function formatNotifTime(dateValue) {
  const dt = new Date(dateValue);
  if (Number.isNaN(dt.getTime())) return '';
  const diffMs = Date.now() - dt.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins} min`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} d`;
  return dt.toLocaleDateString();
}

function resolveNotificationUrl(auxiliary = {}) {
  if (auxiliary.variant) {
    if (auxiliary.product_slug) return `/products/edit/${auxiliary.product_slug}`;
    return '/products';
  }
  if (auxiliary.customer_name) {
    return '/customers';
  }
  if (auxiliary.order) {
    return '/orders';
  }
  if (auxiliary.theme_name) {
    return '/themes/manage';
  }
  if (auxiliary.debit_transaction) {
    return '/channels/kiostore';
  }
  if (auxiliary.devis_order) {
    return '/orders';
  }
  return '/purchases/purchase-orders';
}

function formatNotificationFromApi(item, t) {
  const auxiliary = item?.auxiliary || {};
  const status = auxiliary.status;
  const targetUrl = resolveNotificationUrl(auxiliary);

  if (status === 'out_of_stock') {
    return {
      type: 'stock',
      title: t('notif_out_of_stock'),
      body: auxiliary.product_name || t('notif_variant_stock_changed'),
      url: targetUrl,
    };
  }

  if (status === 'limit_of_stock') {
    return {
      type: 'stock',
      title: t('notif_limit_stock'),
      body: auxiliary.product_name
        ? `${auxiliary.product_name}${typeof auxiliary.available === 'number' ? ` (${auxiliary.available})` : ''}`
        : t('notif_variant_stock_changed'),
      url: targetUrl,
    };
  }

  if (status === 'pre_expiration' || status === 'expired') {
    return {
      type: 'stock',
      title: status === 'expired' ? t('notif_expired') : t('notif_pre_expiration'),
      body: auxiliary.product_name || t('notif_variant_stock_changed'),
      url: targetUrl,
    };
  }

  if (status === 'pre_due_date' || status === 'expired_due_date') {
    return {
      type: 'purchase',
      title: status === 'expired_due_date' ? t('notif_due_expired') : t('notif_due_soon'),
      body: auxiliary.sequence ? `#${auxiliary.sequence}` : t('notif_purchase_due'),
      url: targetUrl,
    };
  }

  if (status === 'updated_theme') {
    return {
      type: 'theme',
      title: t('notif_theme_updated'),
      body: auxiliary.theme_name || t('notif_theme_changed'),
      url: targetUrl,
    };
  }

  if (status === 'ai_credits_empty') {
    return {
      type: 'subscription',
      title: t('notif_ai_credits_empty'),
      body: t('notif_ai_credits_empty_body'),
      url: '/ai-credits',
    };
  }

  if (status === 'ai_human_requested') {
    return {
      type: 'customer',
      title: t('notif_ai_human_requested'),
      body: t('notif_ai_human_requested_body'),
      // Nothing to open in the dashboard: the store replies from its Page inbox (or WhatsApp / Instagram).
      url: null,
    };
  }

  if (auxiliary.order) {
    return {
      type: 'order',
      title: t('notif_order_update'),
      body: auxiliary.customer || t('notif_order_changed'),
      url: targetUrl,
    };
  }

  if (auxiliary.customer || auxiliary.customer_inv) {
    return {
      type: 'customer',
      title: t('notif_customer_update'),
      body: auxiliary.customer_name || t('notif_customer_changed'),
      url: targetUrl,
    };
  }

  return {
    type: 'info',
    title: t('notifications'),
    body: t('notif_new_activity'),
    url: targetUrl,
  };
}


const KEYWORD_HINTS = {
  // ── Profil & mot de passe ─────────────────────────────────────────────────
  profil:           ['nav-profile'],
  profile:          ['nav-profile'],
  compte:           ['nav-profile'],
  account:          ['nav-profile'],
  parametre:        ['nav-profile'],
  parametres:       ['nav-profile'],
  reglage:          ['nav-profile'],
  reglages:         ['nav-profile'],
  settings:         ['nav-profile'],
  'mon compte':     ['nav-profile'],
  'mon profil':     ['nav-profile'],
  'حسابي':          ['nav-profile'],
  'ملف شخصي':       ['nav-profile'],
  'إعدادات الحساب': ['nav-profile'],
  'الإعدادات':      ['nav-profile'],
  'الحساب':         ['nav-profile'],
  avatar:           ['nav-profile'],
  photo:            ['nav-profile'],
  'صورة الملف':     ['nav-profile'],
  'mot de passe':   ['nav-change-password', 'nav-profile'],
  password:         ['nav-change-password', 'nav-profile'],
  mdp:              ['nav-change-password', 'nav-profile'],
  pwd:              ['nav-change-password', 'nav-profile'],
  'changer mot':    ['nav-change-password'],
  'change password':['nav-change-password'],
  'كلمة المرور':    ['nav-change-password', 'nav-profile'],
  'تغيير كلمة':     ['nav-change-password'],
  'تغيير المرور':   ['nav-change-password'],
  'مرور':           ['nav-change-password'],

  // ── Navigation principale ─────────────────────────────────────────────────
  admin:            ['nav-home', 'nav-orders', 'nav-products', 'nav-customers', 'nav-analytics'],
  dashboard:        ['nav-home', 'nav-analytics-overview'],
  'لوحة التحكم':   ['nav-home', 'nav-analytics-overview'],
  'الرئيسية':      ['nav-home'],
  accueil:          ['nav-home'],
  home:             ['nav-home'],

  // ── Commandes ─────────────────────────────────────────────────────────────
  commande:         ['nav-orders', 'nav-abandoned', 'nav-refunded', 'nav-analytics-orders'],
  commandes:        ['nav-orders', 'nav-abandoned', 'nav-refunded', 'nav-analytics-orders'],
  order:            ['nav-orders', 'nav-abandoned', 'nav-refunded', 'nav-analytics-orders'],
  orders:           ['nav-orders', 'nav-abandoned', 'nav-refunded', 'nav-analytics-orders'],
  'الطلبات':        ['nav-orders', 'nav-abandoned', 'nav-refunded', 'nav-analytics-orders'],
  'طلبات':          ['nav-orders', 'nav-analytics-orders'],
  'طلب':            ['nav-orders'],
  'panier':         ['nav-abandoned'],
  'سلة':            ['nav-abandoned'],
  'سلات':           ['nav-abandoned'],
  'سلة مهجورة':     ['nav-abandoned'],
  'paniers abandonnés': ['nav-abandoned'],
  'abandonné':      ['nav-abandoned'],
  abandoned:        ['nav-abandoned'],
  'remboursé':      ['nav-refunded'],
  'remboursement':  ['nav-refunded'],
  refunded:         ['nav-refunded'],
  'مسترد':          ['nav-refunded'],
  'استرداد':        ['nav-refunded'],
  'الطلبات المستردة': ['nav-refunded'],

  // ── Produits ──────────────────────────────────────────────────────────────
  produit:          ['nav-products', 'nav-add-product', 'nav-inventory', 'nav-categories', 'nav-collections'],
  produits:         ['nav-products', 'nav-add-product', 'nav-inventory', 'nav-categories', 'nav-collections'],
  product:          ['nav-products', 'nav-add-product', 'nav-inventory', 'nav-categories', 'nav-collections'],
  catalogue:        ['nav-products', 'nav-categories', 'nav-collections'],
  'المنتجات':       ['nav-products', 'nav-add-product', 'nav-inventory'],
  'منتج':           ['nav-products', 'nav-add-product'],
  'منتجات':         ['nav-products', 'nav-add-product'],
  'كتالوج':         ['nav-products', 'nav-categories', 'nav-collections'],
  'nouveau produit':['nav-add-product'],
  'ajouter produit':['nav-add-product'],
  'add product':    ['nav-add-product'],
  'إضافة منتج':     ['nav-add-product'],
  'منتج جديد':      ['nav-add-product'],
  stock:            ['nav-inventory'],
  stocks:           ['nav-inventory'],
  inventaire:       ['nav-inventory'],
  inventory:        ['nav-inventory'],
  'المخزون':        ['nav-inventory'],
  'مخزون':          ['nav-inventory'],
  sku:              ['nav-inventory'],
  categorie:        ['nav-categories'],
  categories:       ['nav-categories'],
  category:         ['nav-categories'],
  'الفئات':         ['nav-categories'],
  'فئة':            ['nav-categories'],
  'فئات':           ['nav-categories'],
  collection:       ['nav-collections'],
  collections:      ['nav-collections'],
  'المجموعات':      ['nav-collections'],
  'مجموعة':         ['nav-collections'],
  'مجموعات':        ['nav-collections'],

  // ── Clients ───────────────────────────────────────────────────────────────
  client:           ['nav-customers', 'nav-invitations', 'nav-customer-messages', 'nav-analytics-clients'],
  clients:          ['nav-customers', 'nav-invitations', 'nav-customer-messages', 'nav-analytics-clients'],
  customer:         ['nav-customers', 'nav-invitations', 'nav-customer-messages', 'nav-analytics-clients'],
  customers:        ['nav-customers', 'nav-invitations', 'nav-customer-messages', 'nav-analytics-clients'],
  'العملاء':        ['nav-customers', 'nav-invitations', 'nav-customer-messages', 'nav-analytics-clients'],
  'عميل':           ['nav-customers', 'nav-analytics-clients'],
  'عملاء':          ['nav-customers', 'nav-analytics-clients'],
  invitation:       ['nav-invitations'],
  invitations:      ['nav-invitations'],
  'الدعوات':        ['nav-invitations'],
  'دعوة':           ['nav-invitations'],
  'دعوات':          ['nav-invitations'],
  message:          ['nav-customer-messages'],
  messages:         ['nav-customer-messages'],
  'الرسائل':        ['nav-customer-messages'],
  'رسائل':          ['nav-customer-messages'],
  contact:          ['nav-customer-messages', 'nav-support'],
  'رسالة':          ['nav-customer-messages'],
  'formulaire':     ['nav-customer-messages'],

  // ── Équipe / Staff ────────────────────────────────────────────────────────
  equipe:           ['nav-staff'],
  team:             ['nav-staff'],
  staff:            ['nav-staff'],
  employe:          ['nav-staff'],
  employes:         ['nav-staff'],
  permission:       ['nav-staff'],
  permissions:      ['nav-staff'],
  'الفريق':         ['nav-staff'],
  'فريق':           ['nav-staff'],
  'موظف':           ['nav-staff'],
  'صلاحيات':        ['nav-staff'],
  'صلاحية':         ['nav-staff'],

  // ── Newsletter / Bulletins ────────────────────────────────────────────────
  newsletter:       ['nav-newsletter'],
  bulletin:         ['nav-newsletter'],
  bulletins:        ['nav-newsletter'],
  'abonné':         ['nav-newsletter'],
  'النشرة':         ['nav-newsletter'],
  'النشرة البريدية':['nav-newsletter'],
  'بريد':           ['nav-newsletter', 'nav-campaigns'],

  // ── Campagnes e-mail / SMS ────────────────────────────────────────────────
  campagne:         ['nav-campaigns'],
  campagnes:        ['nav-campaigns'],
  campaign:         ['nav-campaigns'],
  email:            ['nav-campaigns', 'nav-newsletter'],
  mail:             ['nav-campaigns', 'nav-newsletter'],
  sms:              ['nav-sms'],
  'campagnes sms':  ['nav-sms'],
  'الحملات':        ['nav-campaigns', 'nav-sms'],
  'حملة':           ['nav-campaigns', 'nav-sms'],
  'حملات':          ['nav-campaigns', 'nav-sms'],
  'حملة البريد':    ['nav-campaigns'],
  'حملات sms':      ['nav-sms'],
  marketing:        ['nav-marketing', 'nav-campaigns', 'nav-newsletter', 'nav-sms'],
  'التسويق':        ['nav-marketing', 'nav-campaigns', 'nav-newsletter', 'nav-sms'],

  // ── Pixels & réseaux sociaux ──────────────────────────────────────────────
  pixel:            ['nav-kiostore', 'nav-marketing'],
  facebook:         ['nav-kiostore', 'nav-marketing'],
  meta:             ['nav-kiostore', 'nav-marketing'],
  'fb pixel':       ['nav-kiostore'],
  tiktok:           ['nav-kiostore', 'nav-marketing'],
  snapchat:         ['nav-kiostore', 'nav-marketing'],
  twitter:          ['nav-kiostore'],
  google:           ['nav-kiostore', 'nav-analytics'],
  gtm:              ['nav-kiostore', 'nav-analytics'],
  'tag manager':    ['nav-kiostore'],
  instagram:        ['nav-kiostore', 'nav-marketing'],

  // ── Paramètres boutique ───────────────────────────────────────────────────
  domaine:          ['nav-kiostore'],
  domain:           ['nav-kiostore'],
  url:              ['nav-kiostore'],
  dns:              ['nav-kiostore'],
  paiement:         ['nav-kiostore'],
  payment:          ['nav-kiostore'],
  'الدفع':          ['nav-kiostore'],
  'طريقة الدفع':    ['nav-kiostore'],
  cod:              ['nav-kiostore'],
  'contre remboursement': ['nav-kiostore'],
  shield:           ['nav-kiostore'],
  fraude:           ['nav-kiostore'],
  google_sheets:    ['nav-kiostore'],
  sheets:           ['nav-kiostore'],
  plan:             ['nav-kiostore'],
  abonnement:       ['nav-kiostore'],
  subscription:     ['nav-kiostore'],
  boutique:         ['nav-kiostore'],
  store:            ['nav-kiostore'],
  'المتجر':         ['nav-kiostore'],
  'البوتيك':        ['nav-kiostore'],

  // ── Wallet ────────────────────────────────────────────────────────────────
  wallet:           ['nav-wallet'],
  portefeuille:     ['nav-wallet'],
  solde:            ['nav-wallet'],
  balance:          ['nav-wallet'],
  retrait:          ['nav-wallet'],
  virement:         ['nav-wallet'],
  withdraw:         ['nav-wallet'],
  'المحفظة':        ['nav-wallet'],
  'رصيد':           ['nav-wallet'],
  'سحب':            ['nav-wallet'],

  // ── Pages boutique / CMS ──────────────────────────────────────────────────
  page:             ['nav-store-pages', 'nav-landing-editor'],
  pages:            ['nav-store-pages', 'nav-landing-editor'],
  cms:              ['nav-store-pages'],
  'pages boutique': ['nav-store-pages'],
  'store pages':    ['nav-store-pages'],
  'الصفحات':        ['nav-store-pages'],
  'صفحات':          ['nav-store-pages'],
  'صفحة':           ['nav-store-pages', 'nav-landing-editor'],

  // ── Thèmes & design ───────────────────────────────────────────────────────
  theme:            ['nav-themes', 'nav-theme-customizer'],
  themes:           ['nav-themes', 'nav-theme-customizer'],
  'القوالب':        ['nav-themes', 'nav-theme-customizer'],
  'قالب':           ['nav-themes', 'nav-theme-customizer'],
  design:           ['nav-theme-customizer', 'nav-themes'],
  couleur:          ['nav-theme-customizer'],
  color:            ['nav-theme-customizer'],
  font:             ['nav-theme-customizer', 'nav-codeed'],
  police:           ['nav-theme-customizer', 'nav-codeed'],
  banner:           ['nav-theme-customizer'],
  'bannière':       ['nav-theme-customizer'],
  hero:             ['nav-theme-customizer'],
  slider:           ['nav-theme-customizer'],
  menu:             ['nav-naveditor'],
  navigation:       ['nav-naveditor'],
  header:           ['nav-naveditor', 'nav-theme-customizer'],
  footer:           ['nav-naveditor', 'nav-theme-customizer'],
  'الشعار':         ['nav-naveditor', 'nav-theme-customizer'],
  'الترويسة':       ['nav-naveditor'],
  'قائمة':          ['nav-naveditor'],
  css:              ['nav-codeed'],
  code:             ['nav-codeed'],
  html:             ['nav-codeed'],
  liquid:           ['nav-codeed'],
  javascript:       ['nav-codeed'],
  js:               ['nav-codeed'],
  landing:          ['nav-landing-editor'],
  'page marketing': ['nav-landing-editor'],
  customizer:       ['nav-theme-customizer'],
  'personnalisation': ['nav-theme-customizer'],
  apparence:        ['nav-theme-customizer'],
  appearance:       ['nav-theme-customizer'],
  'المظهر':         ['nav-theme-customizer'],
  'تخصيص':          ['nav-theme-customizer'],
  'تصميم':          ['nav-theme-customizer'],

  // ── Livraison & logistique ────────────────────────────────────────────────
  livraison:        ['nav-shipping', 'nav-tracking'],
  shipping:         ['nav-shipping'],
  yalidine:         ['nav-shipping'],
  maystro:          ['nav-shipping'],
  ecotrack:         ['nav-shipping'],
  noest:            ['nav-shipping'],
  zr:               ['nav-shipping'],
  'التوصيل':        ['nav-shipping', 'nav-tracking'],
  'الشحن':          ['nav-shipping'],
  'تسليم':          ['nav-shipping', 'nav-tracking'],
  'ولاية':          ['nav-shipping'],
  wilaya:           ['nav-shipping'],
  'المناطق':        ['nav-shipping'],
  zone:             ['nav-shipping'],
  zones:            ['nav-shipping'],
  tarif:            ['nav-shipping'],
  tarifs:           ['nav-shipping'],
  suivi:            ['nav-tracking'],
  tracking:         ['nav-tracking'],
  colis:            ['nav-tracking'],
  'التتبع':         ['nav-tracking'],
  'تتبع':           ['nav-tracking'],
  'طرد':            ['nav-tracking'],
  'طرود':           ['nav-tracking'],

  // ── Réductions & ventes ───────────────────────────────────────────────────
  promo:            ['nav-discounts'],
  'code promo':     ['nav-discounts'],
  'réduction':      ['nav-discounts'],
  discount:         ['nav-discounts'],
  remise:           ['nav-discounts'],
  coupon:           ['nav-discounts'],
  'التخفيضات':      ['nav-discounts'],
  'تخفيض':          ['nav-discounts'],
  'خصم':            ['nav-discounts'],
  'كود':            ['nav-discounts'],
  'كوبون':          ['nav-discounts'],
  'عرض':            ['nav-discounts'],

  // ── Achats / Fournisseurs ─────────────────────────────────────────────────
  achat:            ['nav-purchase-orders', 'nav-invoices', 'nav-suppliers'],
  achats:           ['nav-purchase-orders', 'nav-invoices', 'nav-suppliers'],
  purchase:         ['nav-purchase-orders'],
  'bon de commande':['nav-purchase-orders'],
  'bons de commande': ['nav-purchase-orders'],
  'المشتريات':      ['nav-purchase-orders', 'nav-invoices', 'nav-suppliers'],
  'أوامر الشراء':   ['nav-purchase-orders'],
  'أمر شراء':       ['nav-purchase-orders'],
  facture:          ['nav-invoices'],
  factures:         ['nav-invoices'],
  invoice:          ['nav-invoices'],
  invoices:         ['nav-invoices'],
  'الفواتير':       ['nav-invoices'],
  'فاتورة':         ['nav-invoices'],
  fournisseur:      ['nav-suppliers'],
  fournisseurs:     ['nav-suppliers'],
  supplier:         ['nav-suppliers'],
  suppliers:        ['nav-suppliers'],
  'الموردون':       ['nav-suppliers'],
  'مورد':           ['nav-suppliers'],
  'موردون':         ['nav-suppliers'],

  // ── Affiliation ───────────────────────────────────────────────────────────
  affiliate:        ['nav-affiliate'],
  'affilié':        ['nav-affiliate'],
  affiliation:      ['nav-affiliate'],
  parrainage:       ['nav-affiliate'],
  commission:       ['nav-affiliate'],
  referral:         ['nav-affiliate'],
  'الإحالة':        ['nav-affiliate'],
  'إحالة':          ['nav-affiliate'],
  'عمولة':          ['nav-affiliate'],
  'عمولات':         ['nav-affiliate'],
  'نظام الإحالة':   ['nav-affiliate'],

  // ── Espace personnel ──────────────────────────────────────────────────────
  'espace personnel': ['nav-personal-space'],
  'personal space': ['nav-personal-space'],
  'المساحة الشخصية': ['nav-personal-space'],
  'مساحتي':          ['nav-personal-space'],
  'espace':           ['nav-personal-space'],

  // ── Analytiques ───────────────────────────────────────────────────────────
  analytique:       ['nav-analytics', 'nav-analytics-overview'],
  analytiques:      ['nav-analytics', 'nav-analytics-overview'],
  analytics:        ['nav-analytics', 'nav-analytics-overview'],
  statistique:      ['nav-analytics', 'nav-analytics-overview'],
  statistiques:     ['nav-analytics', 'nav-analytics-overview'],
  rapport:          ['nav-analytics', 'nav-analytics-overview'],
  rapports:         ['nav-analytics', 'nav-analytics-overview'],
  reporting:        ['nav-analytics', 'nav-analytics-overview'],
  'التحليلات':      ['nav-analytics', 'nav-analytics-overview'],
  'تقارير':         ['nav-analytics', 'nav-analytics-overview'],
  'تقرير':          ['nav-analytics', 'nav-analytics-overview'],
  'إحصائيات':       ['nav-analytics', 'nav-analytics-overview'],
  finance:          ['nav-analytics-finance'],
  'finances':       ['nav-analytics-finance'],
  revenu:           ['nav-analytics-finance'],
  revenus:          ['nav-analytics-finance'],
  revenue:          ['nav-analytics-finance'],
  profit:           ['nav-analytics-finance'],
  'chiffre affaires': ['nav-analytics-finance'],
  'ca':             ['nav-analytics-finance'],
  'المالية':        ['nav-analytics-finance'],
  'الإيرادات':      ['nav-analytics-finance'],
  'أرباح':          ['nav-analytics-finance'],

  // ── Support & Notifications ───────────────────────────────────────────────
  support:          ['nav-support'],
  aide:             ['nav-support'],
  help:             ['nav-support'],
  assistance:       ['nav-support'],
  'الدعم':          ['nav-support'],
  'مساعدة':         ['nav-support'],
  'دعم العملاء':    ['nav-support'],
  notification:     ['nav-notifications'],
  notifications:    ['nav-notifications'],
  alerte:           ['nav-notifications'],
  alertes:          ['nav-notifications'],
  'الإشعارات':      ['nav-notifications'],
  'إشعار':          ['nav-notifications'],
  'تنبيه':          ['nav-notifications'],
  'تنبيهات':        ['nav-notifications'],
  activite:         ['nav-activities'],
  activites:        ['nav-activities'],
  activity:         ['nav-activities'],
  activities:       ['nav-activities'],
  historique:       ['nav-activities'],
  'الأنشطة':        ['nav-activities'],
  'نشاط':           ['nav-activities'],
  'السجل':          ['nav-activities'],

  // ── POS & Agent ───────────────────────────────────────────────────────────
  pos:              ['nav-kiopos'],
  caisse:           ['nav-kiopos'],
  'نقطة البيع':     ['nav-kiopos'],
  'كاشير':          ['nav-kiopos'],
  agent:            ['nav-kioagent'],
  chat:             ['nav-kioagent'],
  ia:               ['nav-kioagent'],
  ai:               ['nav-kioagent'],
  bot:              ['nav-kioagent'],
  chatbot:          ['nav-kioagent'],
  'الوكيل':         ['nav-kioagent'],
  'ذكاء':           ['nav-kioagent'],
  credit:           ['nav-ai-credits'],
  credits:          ['nav-ai-credits'],
  crédits:          ['nav-ai-credits'],
  'crédits ia':     ['nav-ai-credits'],
  recharge:         ['nav-ai-credits'],
  'رصيد الذكاء':    ['nav-ai-credits'],
};

function getKeywordHints(query, searchIndex) {
  const q = query.toLowerCase().trim();
  if (!q) return [];
  const matched = new Set();
  Object.entries(KEYWORD_HINTS).forEach(([kw, ids]) => {
    if (kw.startsWith(q) || kw.includes(q) || q.includes(kw)) {
      ids.forEach(id => matched.add(id));
    }
  });
  return [...matched].map(id => searchIndex.find(i => i.id === id)).filter(Boolean);
}

const SCOPE_ICONS = {
  pages:     <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>,
  products:  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M6 2L3 6v14a2 2 0 002 2h14a2 2 0 002-2V6l-3-4z"/><line x1="3" y1="6" x2="21" y2="6"/><path d="M16 10a4 4 0 01-8 0"/></svg>,
  orders:    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  customers: <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="4"/><path d="M4 20c0-4 3.6-7 8-7s8 3 8 7"/></svg>,
};

function ResultItem({ item, active, query, onSelect, onHover }) {
  const highlight = (text) => {
    if (!query) return text;
    const idx = text.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return text;
    return (
      <>
        {text.slice(0, idx)}
        <mark style={{ background: '#fef3c7', color: '#111', borderRadius: 2, padding: '0 1px' }}>
          {text.slice(idx, idx + query.length)}
        </mark>
        {text.slice(idx + query.length)}
      </>
    );
  };

  return (
    <div
      onClick={() => onSelect(item)}
      onMouseEnter={onHover}
      style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 14px', cursor: 'pointer',
        background: active ? '#f5f3ff' : 'transparent',
        borderLeft: `2px solid ${active ? '#6366f1' : 'transparent'}`,
        transition: 'background 0.08s',
      }}
    >
      <div style={{
        width: 28, height: 28, borderRadius: 7,
        background: active ? '#ede9fe' : '#f3f4f6',
        color: active ? '#6366f1' : '#6b7280',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        {SCOPE_ICONS[item.scope]}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {highlight(item.title)}
        </div>
        {item.subtitle && (
          <div style={{ fontSize: 11, color: '#9ca3af', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.subtitle}
          </div>
        )}
      </div>
      {active && (
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="9 18 15 12 9 6"/>
        </svg>
      )}
    </div>
  );
}

function EmptySearchState({ query, onSelect, searchIndex, t }) {
  const hints = useMemo(() => getKeywordHints(query, searchIndex), [query, searchIndex]);

  return (
    <div className="kio-search-dropdown" style={{
      position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
      background: '#fff', border: '1px solid #e5e7eb',
      borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
      zIndex: 1000, overflow: 'hidden',
      animation: 'kio-dd-in 0.15s ease both',
    }}>
      {hints.length > 0 ? (
        /* ── Des hints trouvés : on guide directement ── */
        <>
          <div style={{ padding: '14px 16px 8px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: '#f0f0ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, color: '#6366f1' }}>
              <Icon source={SearchIcon} tone="emphasis" />
            </div>
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#303030' }}>
                <span style={{ color: '#6366f1' }}>"{query}"</span> {t('search_hint_found')}
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>
                {t('search_hint_not_index')}
              </div>
            </div>
          </div>
          {hints.map(item => (
            <ResultItem key={item.id} item={item} active={false} query={query} onSelect={onSelect} onHover={() => {}} />
          ))}
        </>
      ) : (
        /* ── Vraiment rien ── */
        <div style={{ padding: '24px 16px', textAlign: 'center' }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10, background: '#f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px',
          }}>
            <Icon source={SearchIcon} tone="subdued" />
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#303030' }}>
            {t('search_no_results')} <span style={{ color: '#6366f1' }}>"{query}"</span>
          </div>
          <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>
            {t('search_try_other')}
          </div>
        </div>
      )}
    </div>
  );
}

function GlobalSearch({ mobileAutoFocus = false, onClose } = {}) {
  const { lang } = useLanguage();
  const t = useAppT(lang);
  const canUseAgent = useKioAgentAccess();
  const searchIndex = useMemo(() => buildSearchIndex(t, canUseAgent), [t, canUseAgent]);
  const scopes = useMemo(() => [
    { id: 'all',       label: t('scope_all')       },
    { id: 'products',  label: t('scope_products')  },
    { id: 'orders',    label: t('scope_orders')    },
    { id: 'customers', label: t('scope_customers') },
    { id: 'pages',     label: t('scope_pages')     },
  ], [t]);
  const scopeSectionLabels = useMemo(() => ({
    pages: t('scope_pages'),
    products: t('scope_products'),
    orders: t('scope_orders'),
    customers: t('scope_customers'),
  }), [t]);

  const [query, setQuery]       = useState('');
  const [scope, setScope]       = useState('all');
  const [open, setOpen]         = useState(false);
  const [scopeOpen, setScopeOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref      = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Auto-focus when used in mobile panel
  useEffect(() => {
    if (mobileAutoFocus) {
      setTimeout(() => { inputRef.current?.focus(); setOpen(true); }, 80);
    }
  }, [mobileAutoFocus]);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setScopeOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if (
        (e.key === '/' || (e.ctrlKey && e.key === 'k')) &&
        !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)
      ) {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const results = useMemo(() => {
    const q = query.toLowerCase().trim();
    const pool = scope === 'all' ? searchIndex : searchIndex.filter(i => i.scope === scope);
    if (!q) {
      return scope === 'all'
        ? searchIndex.filter(i => i.scope === 'pages').slice(0, 6)
        : pool.slice(0, 8);
    }
    return pool.filter(i =>
      i.title.toLowerCase().includes(q) ||
      (i.subtitle && i.subtitle.toLowerCase().includes(q)) ||
      (Array.isArray(i.keywords) && i.keywords.some((keyword) => keyword.toLowerCase().includes(q) || q.includes(keyword.toLowerCase())))
    ).slice(0, 10);
  }, [query, scope, searchIndex]);

  const grouped = useMemo(() => {
    if (scope !== 'all' || !query.trim()) return null;
    const g = {};
    results.forEach(item => {
      if (!g[item.scope]) g[item.scope] = [];
      g[item.scope].push(item);
    });
    return Object.keys(g).length > 1 ? g : null;
  }, [results, scope, query]);

  const handleSelect = (item) => {
    navigate(item.url, { state: { searchQuery: query || item.title, searchTitle: item.title } });
    setOpen(false);
    setQuery('');
    setActiveIdx(-1);
    onClose?.();
  };

  const handleKeyDown = (e) => {
    if (!open) return;
    if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, results.length - 1)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
    if (e.key === 'Enter' && activeIdx >= 0 && results[activeIdx]) handleSelect(results[activeIdx]);
  };

  const currentScope = scopes.find(s => s.id === scope) || scopes[0];

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', maxWidth: 520 }}>

      {/* ── Input row ── */}
      <div style={{
        display: 'flex', alignItems: 'center',
        background: open ? '#fff' : '#f3f4f6',
        border: `1.5px solid ${open ? '#6366f1' : 'transparent'}`,
        boxShadow: open ? '0 0 0 3px rgba(99,102,241,0.10)' : 'none',
        borderRadius: 10, height: 36,
        transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
      }}>
        {/* Search icon */}
        <div style={{ paddingLeft: 10, paddingRight: 4, flexShrink: 0, display: 'flex', color: '#9ca3af' }}>
          <Icon source={SearchIcon} tone="subdued" />
        </div>

        {/* Text input */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => { setQuery(e.target.value); setOpen(true); setActiveIdx(-1); }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={scope === 'all' ? t('search_placeholder') : t('search_placeholder_scope', currentScope.label)}
          style={{
            flex: 1, background: 'none', border: 'none', outline: 'none',
            fontSize: 13, color: '#111', padding: '0 6px', fontFamily: 'inherit',
          }}
        />

        {/* Clear */}
        {query && (
          <button onClick={() => { setQuery(''); inputRef.current?.focus(); }}
            style={{ paddingRight: 6, background: 'none', border: 'none', cursor: 'pointer', color: '#9ca3af', display: 'flex', alignItems: 'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        )}

        {/* Shortcut hint */}
        {!open && !query && (
          <div style={{ paddingRight: 8, flexShrink: 0 }}>
            <kbd style={{ fontSize: 10, color: '#b0b7c3', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', fontFamily: 'inherit' }}>
              ⌃K
            </kbd>
          </div>
        )}

        {/* Scope selector */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <button
            onClick={() => setScopeOpen(v => !v)}
            style={{
              display: 'flex', alignItems: 'center', gap: 4,
              background: scope !== 'all' ? '#e0e7ff' : '#e9eaec',
              border: 'none', borderRadius: 6, cursor: 'pointer',
              padding: '3px 8px', margin: '0 4px',
              fontSize: 11, fontWeight: 600,
              color: scope !== 'all' ? '#4338ca' : '#374151',
              height: 26, transition: 'background 0.12s',
            }}
          >
            {currentScope.label}
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: scopeOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }}>
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          {scopeOpen && (
            <div className="kio-search-scope-dropdown" style={{
              position: 'absolute', top: 'calc(100% + 6px)', right: 0,
              background: '#fff', border: '1px solid #e5e7eb',
              borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              minWidth: 155, zIndex: 1001, padding: '4px',
              animation: 'kio-dd-in 0.15s ease both',
            }}>
              {scopes.map(s => (
                <button
                  key={s.id}
                  onClick={() => { setScope(s.id); setScopeOpen(false); setActiveIdx(-1); inputRef.current?.focus(); }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    width: '100%', padding: '7px 10px', borderRadius: 7,
                    background: s.id === scope ? '#f0f0ff' : 'none',
                    border: 'none', cursor: 'pointer', textAlign: 'left',
                    fontSize: 13, fontWeight: s.id === scope ? 600 : 400,
                    color: '#303030', transition: 'background 0.1s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = s.id === scope ? '#f0f0ff' : '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = s.id === scope ? '#f0f0ff' : 'none'; }}
                >
                  <span style={{ color: '#9ca3af', display: 'flex' }}>
                    {s.id === 'all'
                      ? <Icon source={SearchIcon} tone="subdued" />
                      : SCOPE_ICONS[s.id]}
                  </span>
                  {s.label}
                  {s.id === scope && (
                    <svg style={{ marginLeft: 'auto' }} width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#4338ca" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Results dropdown ── */}
      {open && results.length > 0 && (
        <div className="kio-search-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 12, boxShadow: '0 12px 32px rgba(0,0,0,0.12)',
          zIndex: 1000, overflow: 'hidden',
          animation: 'kio-dd-in 0.15s ease both',
        }}>
          {/* Section label when no query */}
          {!query.trim() && (
            <div style={{ padding: '10px 14px 4px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              {scope === 'all' ? t('nav_rapide') : currentScope.label}
            </div>
          )}

          {/* Grouped (all + query) */}
          {grouped
            ? Object.entries(grouped).map(([grpScope, items]) => (
                <div key={grpScope}>
                  <div style={{ padding: '8px 14px 3px', fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    {scopeSectionLabels[grpScope]}
                  </div>
                  {items.map(item => (
                    <ResultItem key={item.id} item={item} active={results.indexOf(item) === activeIdx}
                      query={query} onSelect={handleSelect} onHover={() => setActiveIdx(results.indexOf(item))} />
                  ))}
                </div>
              ))
            : results.map((item, idx) => (
                <ResultItem key={item.id} item={item} active={idx === activeIdx}
                  query={query} onSelect={handleSelect} onHover={() => setActiveIdx(idx)} />
              ))
          }

          <div style={{ padding: '8px 14px', borderTop: '1px solid #f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: 11, color: '#9ca3af' }}>{t('results_count', results.length)}</span>
            <span style={{ fontSize: 11, color: '#d1d5db' }}>{t('search_keys_hint')}</span>
          </div>
        </div>
      )}

      {/* ── Empty state ── */}
      {open && query.trim() && results.length === 0 && (
        <EmptySearchState query={query} onSelect={handleSelect} searchIndex={searchIndex} t={t} />
      )}
    </div>
  );
}

function NotificationBell({ storeSlug }) {
  const { lang } = useLanguage();
  const t = useAppT(lang);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [apiUnchecked, setApiUnchecked] = useState(0);
  const ref = useRef(null);
  const unread = Number.isFinite(apiUnchecked) ? Math.max(0, apiUnchecked) : 0;

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const fetchNotifications = async ({ markAllViewed = false, onlyUnread = false, silent = false } = {}) => {
    if (!storeSlug) return;
    if (!silent) {
      setLoading(true);
      setError('');
    }
    try {
      const params = new URLSearchParams();
      params.set('format', 'json');
      if (markAllViewed) params.set('read', 'true');
      if (onlyUnread) params.set('viewed', 'false');
      const res = await apiFetch(`/store/${storeSlug}/all-notifications/?${params.toString()}`);
      if (!res.ok) throw new Error('fetch_notifications_failed');
      const data = await res.json();
      setApiUnchecked(typeof data?.unchecked === 'number' ? data.unchecked : 0);
      if (onlyUnread) return;
      const items = Array.isArray(data?.results) ? data.results : [];
      setNotifs(items.map((item) => {
        const mapped = formatNotificationFromApi(item, t);
        return {
          id: item.id,
          type: mapped.type,
          title: mapped.title,
          body: mapped.body,
          url: mapped.url,
          read: Boolean(item.viewed),
          time: formatNotifTime(item.created),
        };
      }));
    } catch {
      if (!silent) setError(t('notif_error_loading'));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchNotifications();
  }, [open, storeSlug, lang]);

  useEffect(() => {
    if (!storeSlug) return;
    fetchNotifications({ onlyUnread: true, silent: true });
  }, [storeSlug, lang]);

  const markAllRead = async () => {
    await fetchNotifications({ markAllViewed: true });
  };

  const markRead = async (id) => {
    if (!storeSlug) return;
    const previous = notifs;
    setNotifs(ns => ns.map(n => n.id === id ? { ...n, read: true } : n));
    setApiUnchecked((v) => Math.max(0, v - 1));
    try {
      const res = await apiFetch(`/store/${storeSlug}/notifications/?format=json`, {
        method: 'PUT',
        body: JSON.stringify({ id }),
      });
      if (!res.ok) throw new Error('mark_notification_failed');
    } catch {
      setNotifs(previous);
      setApiUnchecked(previous.filter(n => !n.read).length);
      setError(t('notif_error_update'));
    }
  };

  const deleteNotification = async (id) => {
    if (!storeSlug) return;
    const previous = notifs;
    const deletingUnread = previous.find((n) => n.id === id && !n.read);
    setNotifs(ns => ns.filter(n => n.id !== id));
    if (deletingUnread) setApiUnchecked((v) => Math.max(0, v - 1));
    try {
      let res = await apiFetch(`/store/${storeSlug}/notifications/?format=json`, {
        method: 'DELETE',
        body: JSON.stringify({ id }),
      });
      // Some backends ignore DELETE body, fallback to query param
      if (!res.ok) {
        res = await apiFetch(`/store/${storeSlug}/notifications/?format=json&id=${encodeURIComponent(id)}`, {
          method: 'DELETE',
        });
      }
      if (!res.ok) throw new Error('delete_notification_failed');
      await fetchNotifications({ silent: true });
    } catch {
      setNotifs(previous);
      setApiUnchecked(previous.filter(n => !n.read).length);
      setError(t('notif_error_delete'));
    }
  };

  const openNotification = (notif) => {
    if (!notif) return;
    if (!notif.read) markRead(notif.id);
    setOpen(false);
    if (notif.url) navigate(notif.url);
  };

  const clearNotifications = async () => {
    if (!storeSlug) return;
    const previous = notifs;
    setNotifs([]);
    setApiUnchecked(0);
    try {
      const res = await apiFetch(`/store/${storeSlug}/notifications/?format=json&clear=true`, {
        method: 'DELETE',
      });
      if (!res.ok) throw new Error('clear_notifications_failed');
    } catch {
      setNotifs(previous);
      setApiUnchecked(previous.filter(n => !n.read).length);
      setError(t('notif_error_delete'));
    }
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <button
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: 36, height: 36, borderRadius: 8, border: '1px solid #e5e7eb',
          background: open ? '#f3f4f6' : 'none', cursor: 'pointer',
          transition: 'background 0.15s, border-color 0.15s',
        }}
        onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; e.currentTarget.style.borderColor = '#d1d5db'; }}
        onMouseLeave={e => { e.currentTarget.style.background = open ? '#f3f4f6' : 'none'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
      >
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#303030" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        {unread > 0 && (
          <span style={{
            position: 'absolute', top: 4, right: 4,
            width: 8, height: 8, borderRadius: '50%',
            background: '#ef4444', border: '1.5px solid #fff',
            animation: unread > 0 ? 'kio-pulse-notif 2s ease-in-out infinite' : 'none',
          }} />
        )}
      </button>

      {open && (
        <div className="kio-notif-dropdown" style={{
          position: 'absolute', top: 'calc(100% + 8px)', right: 0,
          background: '#fff', border: '1px solid #e5e7eb',
          borderRadius: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          width: 340, zIndex: 999,
          animation: 'kio-dd-in 0.15s ease both',
          overflow: 'hidden',
        }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 16px', borderBottom: '1px solid #f3f4f6' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#111' }}>{t('notifications')}</span>
              {unread > 0 && (
                <span style={{ fontSize: 11, fontWeight: 700, color: '#fff', background: '#ef4444', borderRadius: 20, padding: '1px 7px' }}>
                  {unread}
                </span>
              )}
            </div>
            {unread > 0 && (
              <button onClick={markAllRead} style={{ fontSize: 12, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500, padding: 0 }}>
                {t('mark_all_read')}
              </button>
            )}
          </div>

          {/* List */}
          <div style={{ maxHeight: 360, overflowY: 'auto' }}>
            {loading && (
              <div style={{ padding: '20px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 12 }}>
                {t('notif_loading')}
              </div>
            )}
            {error && (
              <div style={{ padding: '10px 16px', fontSize: 12, color: '#d72c0d', borderBottom: '1px solid #fef2f2' }}>
                {error}
              </div>
            )}
            {notifs.length === 0 ? (
              <div style={{ padding: '32px 16px', textAlign: 'center', color: '#9ca3af', fontSize: 13 }}>
                {t('no_notifications')}
              </div>
            ) : notifs.map(n => (
                (() => {
                  const tone = NOTIF_TONES[n.type] || NOTIF_TONES.info;
                  return (
                <div
                  key={n.id}
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 12,
                    padding: '12px 16px', cursor: 'pointer',
                    background: n.read ? '#fff' : '#fafbff',
                    borderBottom: '1px solid #f9fafb',
                    transition: 'background 0.12s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.read ? '#fff' : '#fafbff'; }}
                >
                  {/* Icon */}
                  <div style={{ width: 32, height: 32, borderRadius: 8, background: tone.bg, color: tone.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {NOTIF_ICONS[n.type] || NOTIF_ICONS.info}
                  </div>
                  {/* Content */}
                  <div style={{ flex: 1, minWidth: 0 }} onClick={() => openNotification(n)}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                      <span style={{ fontSize: 13, fontWeight: n.read ? 500 : 650, color: '#111', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title}</span>
                      {!n.read && <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#3b82f6', flexShrink: 0 }} />}
                    </div>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.body}</p>
                  </div>
                  {/* Time */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{n.time}</span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(n.id);
                      }}
                      aria-label={t('notif_delete')}
                      style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#9ca3af', padding: 0, display: 'flex' }}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                      </svg>
                    </button>
                  </div>
                </div>
                  );
                })()
            ))}
          </div>

          {/* Footer */}
          <div style={{ padding: '10px 16px', borderTop: '1px solid #f3f4f6', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
            <button onClick={clearNotifications} style={{ fontSize: 13, color: '#d72c0d', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}>
              {t('notif_clear_all')}
            </button>
            <button
              onClick={() => {
                setOpen(false);
                navigate('/notifications');
              }}
              style={{ fontSize: 13, color: '#6b7280', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 500 }}
            >
              {t('view_all_notifications')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SearchHighlighter() {
  const location = useLocation();

  useEffect(() => {
    const { searchQuery } = location.state || {};
    if (!searchQuery || searchQuery.length < 2) return;

    const q = searchQuery.toLowerCase();
    let highlighted = [];

    const run = () => {
      const container =
        document.querySelector('.Polaris-Frame__Main') ||
        document.querySelector('main') ||
        document.body;

      // Target ONLY Polaris title-level variants and HTML headings — never descriptions
      const TITLE_SEL = [
        '.Polaris-Text--headingXl', '.Polaris-Text--headingLg',
        '.Polaris-Text--headingMd', '.Polaris-Text--headingSm',
        '.Polaris-Text--headingXs', '.Polaris-Text--bodyMd',
        'h1', 'h2', 'h3', 'h4',
      ].join(',');

      const matched = [];
      container.querySelectorAll(TITLE_SEL).forEach(el => {
        // skip description: bodySm + subdued classes are descriptions
        if (el.classList.contains('Polaris-Text--subdued')) return;
        if (el.textContent.trim().toLowerCase().includes(q)) matched.push(el);
      });

      const seen = new Set();
      const targets = matched
        .filter(el => { if (seen.has(el)) return false; seen.add(el); return true; })
        // remove parents when a child is also in the list
        .filter((el, _, arr) => !arr.some(other => other !== el && el.contains(other)))
        .slice(0, 8);

      if (targets.length === 0) return;

      targets.forEach((el, i) => {
        el.classList.add('kio-search-hl');
        highlighted.push(el);
        if (i === 0) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });

      setTimeout(() => {
        highlighted.forEach(el => el.classList.remove('kio-search-hl'));
        highlighted = [];
      }, 2500);
    };

    const t = setTimeout(run, 350);
    return () => {
      clearTimeout(t);
      highlighted.forEach(el => el.classList.remove('kio-search-hl'));
    };
  }, [location.pathname, location.key]);

  return null;
}

/** /channels/kioagent : accès décidé par kio-api (`user.kioagent`). Le shell s'affiche depuis
 *  l'utilisateur en cache puis le rafraîchit en arrière-plan : un cache antérieur au champ
 *  (`kioagent` absent) attend ce rafraîchissement au lieu de rediriger trop tôt. */
function KioAgentRoute() {
  const { user } = useAuth();
  if (typeof user?.kioagent !== 'boolean') return <PageRouteFallback />;
  return user.kioagent ? <KioAgent /> : <Navigate to="/" replace />;
}

function ProtectedShell() {
  const { user, currentStore, logout, clearStore } = useAuth();
  const { lang, setLang } = useLanguage();
  const t = useAppT(lang);
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const storefrontUrl = useMemo(() => buildStorefrontUrl(currentStore), [currentStore]);
  const { openStorefront, upgradeModal: storefrontUpgradeModal } = useStorefrontAccess();
  const canUseAgent = useKioAgentAccess();

  const salesNavItems = useMemo(() => [
    {
      label: t('nav_orders'),
      url: '/orders',
      icon: OrderIcon,
      subNavigationItems: [
        { label: t('nav_orders_all'), url: '/orders', exactMatch: true, disabled: false },
        { label: t('nav_abandoned'), url: '/orders/abandoned', disabled: false },
        { label: t('nav_refunded'),  url: '/orders/refunded',  disabled: false },
      ],
    },
    {
      label: t('nav_products'),
      url: '/products',
      icon: ProductIcon,
      subNavigationItems: [
        { label: t('nav_products_all'), url: '/products', exactMatch: true, disabled: false },
        { label: t('nav_inventory'),   url: '/products/inventory',   disabled: false },
        { label: t('nav_categories'),  url: '/products/categories',  disabled: false },
        { label: t('nav_collections'), url: '/products/collections', disabled: false },
      ],
    },
    { label: t('nav_discounts'), url: '/discounts', icon: DiscountIcon },
    {
      label: t('nav_customers'),
      url: '/customers',
      icon: PersonIcon,
      subNavigationItems: [
        { label: t('nav_customers_all'), url: '/customers', exactMatch: true, disabled: false },
        { label: t('nav_invitations'), url: '/customers/invitations', disabled: false },
        { label: t('nav_customer_messages'), url: '/customers/messages', disabled: false },
      ],
    },
    { label: t('nav_staff'),     url: '/staff',     icon: TeamIcon },
    // Sits beside the team screen but is the mirror of it: /staff is the owner
    // paying people, /my-earnings is one person reading their own balance. It
    // is in ALWAYS_ALLOWED, so gateItem only ever applies the pack check here.
    { label: t('nav_my_earnings'), url: '/my-earnings', icon: ReceiptIcon },
  ], [t]);

  const growthNavItems = useMemo(() => [
    {
      label: t('nav_marketing'),
      url: '/marketing/bulletins',
      icon: EmailNewsletterIcon,
      subNavigationItems: [
        { label: t('nav_newsletter'),          url: '/marketing/bulletins' },
        { label: t('nav_marketing_campaigns'), url: '/marketing/campagnes' },
        { label: t('nav_sms_campaigns'),       url: '/marketing/sms' },
      ],
    },
    {
      label: t('nav_analytics'),
      url: '/analytics/vue-ensemble',
      icon: ChartVerticalIcon,
      ...(ANALYTICS_SECTIONS.filter((s) => s.visible !== false).length > 1
        ? {
            subNavigationItems: ANALYTICS_SECTIONS.filter((s) => s.visible !== false).map((s) => ({
              label: t(s.labelKey),
              url: `/analytics/${s.id}`,
            })),
          }
        : {}),
    },
  ], [t]);

  const logisticsNavItems = useMemo(() => [
    { label: t('nav_shipping'), url: '/shipping', icon: DeliveryIcon },
    { label: t('nav_tracking'), url: '/tracking', icon: LocationIcon },
    {
      label: t('nav_purchases'),
      url: '/purchases',
      icon: ReceiptIcon,
      subNavigationItems: [
        { label: t('nav_purchase_orders'), url: '/purchases/purchase-orders' },
        { label: t('nav_invoices'),        url: '/purchases/invoices' },
        { label: t('nav_suppliers'),       url: '/purchases/suppliers' },
      ],
    },
    { label: t('nav_affiliate'), url: '/affiliate', icon: AffiliateIcon },
    { label: t('nav_personal_space'), url: '/personal-space', icon: FolderIcon },
  ], [t]);

  // Staff permission gating: a nav item the current staff can't access is shown
  // locked (padlock icon, not clickable). Owners are unrestricted.
  const navSlug = currentStore?.slug;
  const gateItem = useCallback((item) => {
    const allowUrl = (url) => {
      if (!url) return true;
      return isPathAllowed(user, navSlug, url) && isPackPathAllowed(user, url);
    };

    if (!item.subNavigationItems) {
      return allowUrl(item.url) ? item : { ...item, url: undefined, disabled: true };
    }

    const subs = item.subNavigationItems.map((s) => (
      allowUrl(s.url) ? s : { ...s, url: undefined, disabled: true }
    ));
    if (allowUrl(item.url)) return { ...item, subNavigationItems: subs };

    // A child can be reachable while the parent's own destination is not:
    // abandoned carts carry their own right and no order step grants it. Point
    // the parent at that child rather than collapsing the whole branch.
    const firstOpen = subs.find((s) => s.url);
    return firstOpen
      ? { ...item, url: firstOpen.url, subNavigationItems: subs }
      : { ...item, url: undefined, disabled: true, subNavigationItems: undefined };
  }, [user, navSlug]);



const addYoutubeAction = (item, youtubeUrl) => ({
  ...item,
  secondaryActions: [
    ...(item.secondaryActions || []),
    {
      icon: YoutubeIcon,
      accessibilityLabel: 'YouTube',
      tooltip: {
        content: 'YouTube',
      },
      onClick: () => {
        window.open(
          youtubeUrl,
          '_blank',
          'noopener,noreferrer'
        );
      },
    },
  ],
});

const navigation = useMemo(() => (
  <div
    style={{ display: 'contents' }}
    onMouseOver={handleNavPrefetch}
    onFocusCapture={handleNavPrefetch}
  >
    <Navigation location={location.pathname}>

      <Navigation.Section
        items={[
          {
            label: t('nav_home'),
            url: '/',
            icon: HomeIcon,
            exactMatch: true,
          },
        ].map(gateItem)}
      />

      <Navigation.Section
        title={t('nav_section_sales')}
        items={salesNavItems.map((item) => {
          const gatedItem = gateItem(item);

          if (gatedItem.url === '/orders') {
            return addYoutubeAction(
              gatedItem,
              'https://youtu.be/72tMqQc9FEg?si=uqhf3yXlJC0w33g9'
            );
          }

          if (gatedItem.url === '/products') {
            return addYoutubeAction(
              gatedItem,
              'https://youtu.be/E2JceGnWxgI?si=G-iPRXb-ootiTT08'
            );
          }

          if (gatedItem.url === '/staff') {
            return addYoutubeAction(
              gatedItem,
              'https://youtu.be/Q5DvYG_Jv2c?si=s-d--xpBC4XVpWHd'
            );
          }

          return gatedItem;
        })}
      />

      {/* <Navigation.Section
        title={t('nav_section_growth')}
        items={growthNavItems.map((item) => {
          const gatedItem = gateItem(item);

          if (gatedItem.url === '/staff') {
            return addYoutubeAction(
              gatedItem,
              'YOUR_YOUTUBE_LINK_EQUIPE'
            );
          }

          return gatedItem;
        })}
      /> */}

      <Navigation.Section
        title={t('nav_section_channels')}
        items={[
          {
            label: t('nav_kiostore'),
            url: '/channels/kiostore',
            icon: StoreIcon,
            ...(storefrontUrl
              ? {
                  secondaryActions: [
                    {
                      icon: ViewIcon,
                      accessibilityLabel: t('view_store'),
                      tooltip: {
                        content: t('view_store'),
                      },
                      onClick: () =>
                        openStorefront(storefrontUrl),
                    },
                  ],
                }
              : {}),
          },

          {
            label: t('nav_funnels'),
            url: '/funnels',
            icon: TargetIcon,
          },

          {
            label: t('nav_kiopos'),
            url: '/channels/kiopos',
            icon: PointOfSaleIcon,
          },

          ...(canUseAgent
            ? [
                {
                  label: t('nav_kioagent'),
                  url: '/channels/kioagent',
                  icon: ChatIcon,
                },
              ]
            : []),
        ]
          .map(gateItem)
          .map((item) => {
            if (item.url === '/channels/kiostore') {
              return addYoutubeAction(
                item,
                'https://youtu.be/GgUVqew1MYQ?si=0xCjk6Pw74rYPpcc'
              );
            }

            return item;
          })}
      />

      <Navigation.Section
        title={t('nav_section_logistics')}
        items={logisticsNavItems.map((item) => {
          const gatedItem = gateItem(item);

          if (gatedItem.url === '/shipping') {
            return addYoutubeAction(
              gatedItem,
              'https://youtu.be/0nw71O6oLxk?si=_Cn6c-G0aVgUSH-v'
            );
          }

          return gatedItem;
        })}
      />

    </Navigation>
  </div>
), [
  t,
  location.pathname,
  salesNavItems,
  growthNavItems,
  logisticsNavItems,
  storefrontUrl,
  gateItem,
  canUseAgent,
  openStorefront,
]);
  // Must be before early returns — Rules of Hooks
  useEffect(() => {
    setMobileSearchOpen(false);
    setMobileNavOpen(false);
  }, [location.pathname]);

  // Warm the heaviest chunks once the browser is idle after first paint.
  useEffect(() => {
    const idle = window.requestIdleCallback ?? ((cb) => setTimeout(cb, 2000));
    const cancel = window.cancelIdleCallback ?? clearTimeout;
    const id = idle(() => {
      ['/orders', '/products', '/analytics/vue-ensemble', '/customers'].forEach(preloadRoute);
    });
    return () => cancel(id);
  }, []);

  if (!user) return <Navigate to="/login" replace />;
  if (!user.isConfirmed) return <Navigate to="/verify" replace />;
  if (!currentStore) return <Navigate to="/store-select" replace />;

  

  const topBarMarkup = (
    <TopBar
      showNavigationToggle
      onNavigationToggle={() => setMobileNavOpen(v => !v)}
      searchField={<GlobalSearch />}
      userMenu={<UserDropdown user={user} currentStore={currentStore} logout={logout} clearStore={clearStore} />}
      secondaryMenu={
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {/* Search icon — visible on mobile only (CSS toggles display) */}
          <button
            className="kio-mobile-search-toggle"
            onClick={() => setMobileSearchOpen(v => !v)}
            aria-label={t('search_aria')}
            style={{
              display: 'none',
              alignItems: 'center', justifyContent: 'center',
              width: 34, height: 34, borderRadius: 8, flexShrink: 0,
              border: `1.5px solid ${mobileSearchOpen ? '#6366f1' : '#e5e7eb'}`,
              background: mobileSearchOpen ? '#f0f0ff' : 'none',
              cursor: 'pointer',
              color: mobileSearchOpen ? '#6366f1' : '#303030',
            }}
          >
            <Icon source={SearchIcon} tone={mobileSearchOpen ? 'emphasis' : 'subdued'} />
          </button>
          <a href="https://www.youtube.com/@kio_dz" target="_blank" rel="noopener noreferrer">
            <Icon source={YoutubeIcon} />
          </a>
           <a
              href="https://wa.me/213550279969"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="WhatsApp"
            >
            <Icon source={WhatsAppIcon} />
          </a>
          <NotificationBell storeSlug={currentStore?.slug} />
          <div className="kio-topbar-lang">
            <LangDropdown lang={lang} setLang={setLang} />
          </div>
        </div>
      }
    />
  );

  return (
    <Frame
      topBar={topBarMarkup}
      showMobileNavigation={mobileNavOpen}
      onNavigationDismiss={() => setMobileNavOpen(false)}
      logo={{
        width: 60,
        topBarSource: kioLogo,
        contextualSaveBarSource: kioLogo,
        url: '/',
        accessibilityLabel: 'KIO Store',
      }}
      navigation={navigation}
    >
      <SearchHighlighter />

      {/* ── Mobile search slide-down panel ── */}
      {mobileSearchOpen && (
        <div className="kio-mobile-search-panel" style={{
          position: 'fixed',
          top: 'var(--pg-top-bar-height, 3.5rem)',
          left: 0, right: 0,
          background: '#fff',
          borderBottom: '1px solid #e5e7eb',
          padding: '10px 12px',
          zIndex: 9998,
          boxShadow: '0 6px 20px rgba(0,0,0,0.10)',
          animation: 'kio-slide-down 0.18s ease both',
        }}>
          <GlobalSearch mobileAutoFocus onClose={() => setMobileSearchOpen(false)} />
        </div>
      )}

      <TrialBanner user={user} lang={lang} />
      <ContactVerificationBanner user={user} lang={lang} />
      <ThemeUpdateNotificationBanner storeSlug={currentStore?.slug} lang={lang} />
      <PwaInstallBanner lang={lang} />
      {storefrontUpgradeModal}

      <RouteTransitionShell>
        {canAccessPath(user, currentStore.slug, location.pathname) ? (
        <Routes>
          <Route path="/"        element={<Home />} />
          <Route path="/analytics" element={<AnalyticsLayout />}>
            <Route index element={<Navigate to="/analytics/vue-ensemble" replace />} />
            <Route path="vue-ensemble" element={<Analytics />} />
            <Route path="finance" element={<AnalyticsFinance />} />
            <Route path="commandes" element={<AnalyticsOrders />} />
            <Route path="clients" element={<AnalyticsClients />} />
            <Route path="produits" element={<AnalyticsProducts />} />
            <Route path="canaux" element={<AnalyticsChannels />} />
            <Route path="*" element={<Navigate to="/analytics/vue-ensemble" replace />} />
          </Route>
          <Route path="/orders" element={<Orders />} />
          <Route path="/orders/abandoned" element={<AbandonedCarts />} />
          <Route path="/orders/refunded" element={<RefundedOrders />} />
          <Route path="/products" element={<Products />} />
          <Route path="/products/inventory" element={<Inventory />} />
          <Route path="/products/categories" element={<Categories />} />
          <Route path="/products/collections" element={<Collections />} />
          <Route path="/products/add" element={<AddProduct />} />
          <Route path="/products/edit/:productId" element={<AddProduct />} />
          <Route path="/discounts"        element={<Discounts />} />
          <Route path="/customers/*" element={<Customers />} />
          <Route path="/staff"            element={<Staff />} />
          <Route path="/my-earnings"      element={<MyEarnings />} />
          <Route path="/marketing" element={<Navigate to="/marketing/bulletins" replace />} />
          <Route path="/marketing/bulletins" element={<Newsletter />} />
          <Route path="/marketing/campagnes" element={<MarketingCampaigns />} />
          <Route path="/marketing/sms"       element={<SmsCampaigns />} />
          <Route path="/channels/kiostore" element={<KioStore />} />
          <Route path="/wallet" element={<KioWallet />} />
          <Route path="/channels/kiopos"   element={<KioPos />} />
          <Route path="/channels/kioagent" element={<KioAgentRoute />} />
          <Route path="/ai-credits" element={<AiCredits />} />
          <Route path="/themes/manage"       element={<ThemesManager />} />
          <Route path="/funnels"             element={<FunnelsManager />} />
          <Route path="/themes/add"          element={<AddTheme />} />
          <Route path="/themes/code-editor"    element={<CodeEditor />} />
          <Route path="/themes/landing-editor" element={<LandingPageEditor />} />
          <Route path="/themes/editor"              element={<Navigate to="/themes/customizer" replace />} />
          <Route path="/themes/customizer"          element={<ThemeCustomizer />} />
          <Route path="/channels/kiostore/navigation" element={<NavigationEditor />} />
          <Route path="/channels/kiostore/pages/new" element={<StorePages />} />
          <Route path="/channels/kiostore/pages/:pageSlug" element={<StorePages />} />
          <Route path="/channels/kiostore/pages" element={<StorePages />} />
          <Route path="/shipping" element={<Shipping />} />
          <Route path="/shipping/zones/:carrierId" element={<CarrierZonesPage />} />
          <Route path="/shipping/noest" element={<NoestDashboard />} />
          <Route path="/shipping/ecotrack/:carrierId" element={<EcotrackDashboard />} />
          <Route path="/tracking" element={<Tracking />} />
          <Route path="/purchases" element={<Navigate to="/purchases/purchase-orders" replace />} />
          <Route path="/purchases/purchase-orders" element={<PurchaseOrders />} />
          <Route path="/purchases/invoices" element={<Invoices />} />
          <Route path="/purchases/suppliers" element={<Suppliers />} />
          <Route path="/affiliate" element={<Affiliate />} />
          <Route path="/personal-space" element={<PersonalSpace />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/support" element={<Support />} />
          <Route path="/notifications" element={<AllNotifications />} />
          <Route path="/activities" element={<AllActivity />} />
          <Route path="*"         element={<Navigate to="/" replace />} />
        </Routes>
        ) : (
          <Navigate to={firstAllowedPath(user, currentStore.slug)} replace />
        )}
      </RouteTransitionShell>
    </Frame>
  );
}

function StaffSignupRedirect() {
  const [searchParams] = useSearchParams();
  return <Navigate to={`/signup?${searchParams.toString()}`} replace />;
}

function AffiliateSignupRedirect() {
  const [searchParams] = useSearchParams();
  const code = searchParams.get('aff');
  const dest = code ? `/signup?aff=${encodeURIComponent(code)}` : '/signup';
  return <Navigate to={dest} replace />;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user?.isConfirmed) return <Navigate to="/store-select" replace />;
  if (user && !user.isConfirmed) return <Navigate to="/verify" replace />;
  return children;
}

function VerifyRoute({ children }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.isConfirmed && !getPendingContactVerification(user)) return <Navigate to="/store-select" replace />;
  return children;
}


/**
 * Accepts a pending staff invitation for an already-existing account.
 *
 * The token is captured from the URL on import (see utils/staffInvitation), so
 * here we only consume it once the user is authenticated: POST it to the accept
 * endpoint, refresh stores + user, then drop the user into the newly joined
 * store. Renders nothing. Runs globally (sibling of the routes) so it works
 * regardless of which page the user lands on after login.
 */
function StaffInvitationHandler() {
  const { user, refreshStores, refreshUser, selectStore } = useAuth();
  const navigate = useNavigate();
  const processingRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    const token = getPendingStaffToken();
    if (!token || processingRef.current) return;
    processingRef.current = true;

    (async () => {
      try {
        const res = await apiAcceptStaffInvitation(token);
        const stores = await refreshStores(user.username);
        await refreshUser();
        const joined = Array.isArray(stores)
          ? stores.find((s) => s.slug === res.store)
          : null;
        if (joined) {
          selectStore(joined);
          navigate('/', { replace: true });
        } else {
          navigate('/store-select', { replace: true });
        }
      } catch (e) {
        // Invalid / expired / already accepted / email mismatch — surface it on
        // the store picker where the user lands.
        setStaffInvitationError(e?.message || "Impossible d'accepter l'invitation.");
        navigate('/store-select', { replace: true });
      } finally {
        clearPendingStaffToken();
        processingRef.current = false;
      }
    })();
  }, [user, refreshStores, refreshUser, selectStore, navigate]);

  return null;
}

function AppRoutes() {
  return (
    <>
      <PaymentReturnHandler />
      <Suspense fallback={<PageRouteFallback />}>
      <PhoneRequiredGate>
      <Routes>
        <Route path="/login"        element={<PublicRoute><Login /></PublicRoute>} />
        <Route path="/signup"       element={<PublicRoute><Signup /></PublicRoute>} />
        <Route path="/admin/sign-up" element={<StaffSignupRedirect />} />
        <Route path="/admin/sign-up/go" element={<AffiliateSignupRedirect />} />
        <Route path="/verify"       element={<VerifyRoute><VerifyAccount /></VerifyRoute>} />
        <Route path="/store-select" element={<StoreSelect />} />
        <Route path="/store-create" element={<StoreOnboarding />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/*"            element={<ProtectedShell />} />
      </Routes>
      </PhoneRequiredGate>
    </Suspense>
    </>
  );
}

export default function App() {
  return (
    <GoogleOAuthShell>
      <AppProvider i18n={{}}>
        <AuthProvider>
          <LanguageProvider>
            <Router>
              <StaffInvitationHandler />
              <AppRoutes />
            </Router>
          </LanguageProvider>
        </AuthProvider>
      </AppProvider>
    </GoogleOAuthShell>
  );
}
